import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Copy, Loader2, RefreshCw, ShieldAlert, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { getPlan } from "@/lib/api";
import SupplementGuide from "@/components/SupplementGuide";

const GRADE_STYLE = {
  A: "bg-sage text-bone",
  B: "bg-ink text-bone",
  C: "bg-clay/80 text-bone",
  D: "bg-slatem text-bone",
  X: "bg-clay text-bone",
};
const GRADE_LABEL = { A: "Strong guidance", B: "Good evidence", C: "Promising / mixed", D: "Early signal", X: "Safety concern" };
const SIGNAL_LABEL = {
  food_variety: { low: "Food variety: low", moderate: "Food variety: moderate", good: "Food variety: good" },
  protein: { covered: "Protein: covered", check: "Protein: check anchors" },
  omega3: { covered: "Omega-3: food covered", opportunity: "Omega-3: food-first opportunity" },
  movement: { on_track: "Movement: on track", build: "Movement: build consistency", start: "Movement: start small" },
  recovery: { protect: "Recovery: protect routine", priority: "Recovery: priority" },
};

const Field = ({ label, children }) => (
  <div>
    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slatem mb-1.5">{label}</p>
    <p className="text-sm sm:text-base leading-relaxed text-ink/85">{children}</p>
  </div>
);

export default function Plan() {
  const { id } = useParams();
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    getPlan(id).then(setPlan).catch(() => setError(true));
  }, [id]);

  const share = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      toast.success("Plan link copied — it stays live for 30 days.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy the link.");
    }
  };

  if (error)
    return (
      <main className="min-h-screen pt-40 px-6 text-center" data-testid="plan-error">
        <h1 className="font-serif text-4xl font-extrabold uppercase tracking-tight mb-4">This plan has expired or never existed.</h1>
        <p className="text-slatem mb-8">Anonymous plans live for 30 days. Build a fresh one in two minutes.</p>
        <Link to="/check-in" data-testid="plan-error-cta" className="inline-flex items-center gap-2 rounded-full bg-ink text-bone px-7 py-3.5 text-sm font-semibold hover:bg-clay transition-colors min-h-[44px]">Take the test</Link>
      </main>
    );

  if (!plan)
    return (
      <main className="min-h-screen pt-48 flex justify-center" data-testid="plan-loading">
        <Loader2 className="h-8 w-8 animate-spin text-clay" />
      </main>
    );

  const safe = plan.safety_state === "clinician_safe";

  return (
    <main className="min-h-screen pt-28 pb-24 px-4 sm:px-6" data-testid="plan-page">
      <div className="mx-auto max-w-3xl">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-clay mb-4">your result — the 10-point plan</p>
          <h1 className="font-serif text-4xl sm:text-6xl font-extrabold uppercase tracking-tight leading-[0.95] mb-6" data-testid="plan-title">
            {safe ? "Your road to Zinda Banda — clinician-safe mode." : "This is how you truly become Zinda Banda."}
          </h1>

          <div className={`flex items-start gap-3 rounded-2xl border p-4 sm:p-5 mb-8 ${safe ? "border-clay/40 bg-clay/5" : "border-sage/40 bg-sage/5"}`} data-testid="plan-safety-banner">
            {safe ? <ShieldAlert className="h-5 w-5 text-clay shrink-0 mt-0.5" /> : <ShieldCheck className="h-5 w-5 text-sage shrink-0 mt-0.5" />}
            <p className="text-sm leading-relaxed text-ink/80">
              {safe
                ? "You flagged a medicine, condition, surgery or unexplained symptom. This plan contains no supplement suggestions — bring it to your doctor or pharmacist."
                : "General wellbeing information — not medical advice, diagnosis, or treatment. Supplements can interact with medicines; check with a pharmacist if unsure."}
            </p>
          </div>

          <div className="flex flex-wrap gap-2 mb-6" data-testid="plan-signals">
            {Object.entries(SIGNAL_LABEL).map(([k, m]) =>
              plan.signals[k] && m[plan.signals[k]] ? (
                <span key={k} className="rounded-full border border-ink/15 bg-sand px-3.5 py-1.5 text-xs font-medium text-ink/75">{m[plan.signals[k]]}</span>
              ) : null
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3 mb-14">
            <button onClick={share} data-testid="plan-share-button"
              className="inline-flex items-center gap-2 rounded-full bg-ink text-bone px-6 py-3 text-sm font-semibold hover:bg-clay transition-colors min-h-[44px]">
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? "Copied" : "Copy share link"}
            </button>
            <Link to="/check-in" data-testid="plan-retake-button"
              className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-6 py-3 text-sm font-semibold hover:border-clay hover:text-clay transition-colors min-h-[44px]">
              <RefreshCw className="h-4 w-4" /> Retest in 4 weeks
            </Link>
          </div>
        </motion.div>

        <SupplementGuide supplements={plan.supplements} safetyState={plan.safety_state} />

        <div className="space-y-5">
          {plan.cards.map((c, i) => (
            <motion.article key={c.key} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.5, delay: 0.03 * i, ease: [0.16, 1, 0.3, 1] }}
              className="rounded-3xl border border-ink/12 bg-bone p-6 sm:p-8 hover:border-clay/35 transition-colors duration-300"
              data-testid={`plan-card-${c.n}`}>
              <div className="flex items-start justify-between gap-4 mb-5">
                <div className="flex items-center gap-4">
                  <span className="font-serif text-4xl font-bold text-ink/15">{String(c.n).padStart(2, "0")}</span>
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slatem">{c.tag}</p>
                    <h2 className="font-serif text-xl sm:text-2xl font-bold uppercase tracking-tight leading-tight">{c.title}</h2>
                  </div>
                </div>
                <span className={`shrink-0 rounded-full px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] ${GRADE_STYLE[c.grade]}`} data-testid={`plan-card-${c.n}-grade`}>
                  {c.grade} · {GRADE_LABEL[c.grade]}
                </span>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Why it appeared">{c.why}</Field>
                <Field label="Do this">{c.action}</Field>
                <Field label="Make it easier">{c.easier}</Field>
                <Field label="Track">{c.track}</Field>
              </div>
              <div className="mt-5 pt-5 border-t border-ink/10">
                <p className="text-xs text-slatem leading-relaxed"><span className="font-mono uppercase tracking-[0.18em] text-sage mr-2">evidence</span>{c.evidence}</p>
                {c.safety && <p className="mt-2 text-xs text-clay/90 leading-relaxed"><span className="font-mono uppercase tracking-[0.18em] mr-2">safety</span>{c.safety}</p>}
              </div>
            </motion.article>
          ))}
        </div>

        <p className="mt-12 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-slatem" data-testid="plan-footer-note">
          ruleset {plan.ruleset_version} · anonymous · expires {new Date(plan.expires_at).toLocaleDateString()}
        </p>
      </div>
    </main>
  );
}
